import { jsPDF } from 'jspdf';
import { initializePdfWithUtf8Font, getUtf8FontName } from './pdfFontLoader';

// Branding configuration interface
export interface PdfBrandingConfig {
  logoHeight: number;       // Logo height in mm (width auto-calculated from ratio)
  headerTitle: string;      // Text next to logo (e.g., "Kompakt")
  slogan: string;           // Footer slogan
  websiteUrl: string;       // Footer URL
}

// Default branding config
export const defaultBrandingConfig: PdfBrandingConfig = {
  logoHeight: 24,
  headerTitle: 'Kompakt',
  slogan: 'Keine komplizierte Fachsprache. Keine versteckten Kosten.',
  websiteUrl: 'https://klartech-mueritz.de',
};

// Get branding config from localStorage
export function getBrandingConfig(): PdfBrandingConfig {
  try {
    const stored = localStorage.getItem('kompakt_pdf_branding');
    if (stored) {
      return { ...defaultBrandingConfig, ...JSON.parse(stored) };
    }
  } catch {
    // ignore
  }
  return defaultBrandingConfig;
}

// Save branding config to localStorage
export function saveBrandingConfig(config: PdfBrandingConfig): void {
  localStorage.setItem('kompakt_pdf_branding', JSON.stringify(config));
}

interface AnalysisData {
  fileName?: string;
  summary?: string;
  keywords?: string[];
  sentiment?: {
    overall: string;
    positive: number;
    neutral: number;
    negative: number;
  };
  themes?: Array<{ theme: string; sentiment: string }>;
  language?: string;
  createdAt?: string | Date;
}

interface ComparisonData {
  fileName1?: string;
  fileName2?: string;
  summary?: string;
  similarities?: string[];
  differences?: string[];
  uniqueDoc1?: string[];
  uniqueDoc2?: string[];
}

interface QAData {
  question: string;
  answer: string;
  fileName?: string;
}

// Helper to load image as base64 + read its intrinsic size
async function loadImageForPdf(url: string): Promise<{ dataUrl: string; width: number; height: number } | null> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    const dataUrl = await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });

    if (!dataUrl) return null;

    const size = await new Promise<{ width: number; height: number } | null>((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    });

    if (!size) return null;

    return { dataUrl, width: size.width, height: size.height };
  } catch {
    return null;
  }
}

// Format date in German
function formatDateDE(date: Date): string {
  return new Intl.DateTimeFormat('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

// Professional PDF builder with compact spacing
class PdfBuilder {
  private doc: jsPDF;
  private y: number;
  private margin = 18;
  private pageWidth: number;
  private pageHeight: number;
  private contentWidth: number;
  private lineHeight = 4.5; // Compact line height

  constructor(doc: jsPDF, startY: number) {
    this.doc = doc;
    this.y = startY;
    this.pageWidth = doc.internal.pageSize.getWidth();
    this.pageHeight = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - 2 * this.margin;
  }

  private checkPageBreak(neededHeight: number) {
    if (this.y + neededHeight > this.pageHeight - 25) {
      this.doc.addPage();
      this.y = this.margin;
    }
  }

  addSectionHeader(text: string) {
    this.checkPageBreak(12);
    this.doc.setFontSize(13);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(8, 145, 178); // Brand color
    this.doc.text(text, this.margin, this.y);
    this.y += 6;
    this.doc.setTextColor(0, 0, 0);
  }

  addLabelValue(label: string, value: string) {
    this.checkPageBreak(6);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(label, this.margin, this.y);
    const labelWidth = this.doc.getTextWidth(label) + 2;
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(value, this.margin + labelWidth, this.y);
    this.y += this.lineHeight;
  }

  addParagraph(text: string, fontSize = 10) {
    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', 'normal');
    const lines = this.doc.splitTextToSize(text, this.contentWidth);
    const neededHeight = lines.length * this.lineHeight;
    this.checkPageBreak(neededHeight);
    this.doc.text(lines, this.margin, this.y);
    this.y += neededHeight + 1;
  }

  addBulletList(items: string[], fontSize = 10) {
    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', 'normal');
    const bulletIndent = 4;
    
    for (const item of items) {
      const lines = this.doc.splitTextToSize(item, this.contentWidth - bulletIndent);
      const neededHeight = lines.length * this.lineHeight;
      this.checkPageBreak(neededHeight);
      
      this.doc.text('•', this.margin, this.y);
      this.doc.text(lines, this.margin + bulletIndent, this.y);
      this.y += neededHeight;
    }
  }

  addSentimentBar(positive: number, neutral: number, negative: number) {
    this.checkPageBreak(14);
    const barY = this.y;
    const barHeight = 6;
    const barWidth = this.contentWidth - 60;
    const startX = this.margin + 60;
    
    // Labels
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    
    // Draw stacked bar
    let currentX = startX;
    
    // Positive (green)
    if (positive > 0) {
      const w = (positive / 100) * barWidth;
      this.doc.setFillColor(34, 197, 94);
      this.doc.rect(currentX, barY, w, barHeight, 'F');
      currentX += w;
    }
    
    // Neutral (gray)
    if (neutral > 0) {
      const w = (neutral / 100) * barWidth;
      this.doc.setFillColor(156, 163, 175);
      this.doc.rect(currentX, barY, w, barHeight, 'F');
      currentX += w;
    }
    
    // Negative (red)
    if (negative > 0) {
      const w = (negative / 100) * barWidth;
      this.doc.setFillColor(239, 68, 68);
      this.doc.rect(currentX, barY, w, barHeight, 'F');
    }
    
    // Legend below bar
    this.y = barY + barHeight + 3;
    this.doc.setFontSize(8);
    
    let legendX = startX;
    this.doc.setFillColor(34, 197, 94);
    this.doc.rect(legendX, this.y, 3, 3, 'F');
    this.doc.text(`Positiv ${positive}%`, legendX + 4, this.y + 2.5);
    
    legendX += 35;
    this.doc.setFillColor(156, 163, 175);
    this.doc.rect(legendX, this.y, 3, 3, 'F');
    this.doc.text(`Neutral ${neutral}%`, legendX + 4, this.y + 2.5);
    
    legendX += 35;
    this.doc.setFillColor(239, 68, 68);
    this.doc.rect(legendX, this.y, 3, 3, 'F');
    this.doc.text(`Negativ ${negative}%`, legendX + 4, this.y + 2.5);
    
    this.y += 6;
  }

  addSpace(mm: number) {
    this.y += mm;
  }

  getY() {
    return this.y;
  }
}

// Create branded PDF header
async function createBrandedPDF(title: string, config: PdfBrandingConfig) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const headerHeight = 38;

  // Header background
  doc.setFillColor(8, 145, 178);
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  // Load logo
  const logoUrl = '/images/logo-klartech.png';
  const logo = await loadImageForPdf(logoUrl);

  doc.setTextColor(255, 255, 255);

  if (logo) {
    const logoX = 14;
    const logoY = 7;
    const targetLogoH = config.logoHeight;
    const ratio = logo.width / logo.height;
    const targetLogoW = targetLogoH * ratio;

    doc.addImage(logo.dataUrl, 'PNG', logoX, logoY, targetLogoW, targetLogoH);

    // Header title next to logo
    const textX = logoX + targetLogoW + 6;
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text(config.headerTitle, textX, 20);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(title, textX, 28);
  } else {
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text(config.headerTitle, 18, 20);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(title, 18, 28);
  }

  doc.setTextColor(0, 0, 0);

  return doc;
}

// Add footer to PDF
function addFooter(doc: jsPDF, config: PdfBrandingConfig) {
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const footerY = pageHeight - 12;

  // Separator line
  doc.setDrawColor(200, 200, 200);
  doc.line(18, footerY - 3, pageWidth - 18, footerY - 3);

  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(`Klartech Müritz – ${config.slogan}`, pageWidth / 2, footerY, { align: 'center' });
  doc.text(config.websiteUrl, pageWidth / 2, footerY + 4, { align: 'center' });
}

// Helper to add single analysis content to builder
function addAnalysisContent(builder: PdfBuilder, analysis: AnalysisData, includeHeader = true) {
  if (includeHeader) {
    builder.addSectionHeader('Dokument-Information');
  }
  if (analysis.fileName) {
    builder.addLabelValue('Dateiname:', analysis.fileName);
  }
  const date = analysis.createdAt ? new Date(analysis.createdAt) : new Date();
  builder.addLabelValue('Analysiert:', formatDateDE(date));
  builder.addSpace(4);

  // Summary
  if (analysis.summary) {
    builder.addSectionHeader('Zusammenfassung');
    builder.addParagraph(analysis.summary);
    builder.addSpace(3);
  }

  // Keywords
  if (analysis.keywords && analysis.keywords.length > 0) {
    builder.addSectionHeader('Keywords');
    builder.addParagraph(analysis.keywords.join('  •  '));
    builder.addSpace(3);
  }

  // Sentiment Analysis
  if (analysis.sentiment) {
    builder.addSectionHeader('Sentiment-Analyse');
    builder.addLabelValue('Gesamtbewertung:', analysis.sentiment.overall);
    builder.addSpace(2);
    builder.addSentimentBar(
      analysis.sentiment.positive,
      analysis.sentiment.neutral,
      analysis.sentiment.negative
    );
    builder.addSpace(3);
  }

  // Themes
  if (analysis.themes && analysis.themes.length > 0) {
    builder.addSectionHeader('Hauptthemen');
    builder.addBulletList(
      analysis.themes.map(t => `${t.theme} (${t.sentiment})`)
    );
  }
}

export async function exportAnalysisToPDF(analysis: AnalysisData) {
  const config = getBrandingConfig();
  const doc = await createBrandedPDF('Dokumentenanalyse', config);
  const builder = new PdfBuilder(doc, 46);

  addAnalysisContent(builder, analysis);

  addFooter(doc, config);

  const fileName = analysis.fileName
    ? `${analysis.fileName.replace(/\.[^/.]+$/, '')}_Analyse.pdf`
    : `Analyse_${new Date().toISOString().split('T')[0]}.pdf`;

  doc.save(fileName);
}

// Export multiple analyses into a single combined PDF
export async function exportBatchAnalysesToPDF(analyses: AnalysisData[]) {
  if (analyses.length === 0) return;

  const config = getBrandingConfig();
  const doc = await createBrandedPDF(`Batch-Analyse (${analyses.length} Dokumente)`, config);
  
  // Table of contents
  let y = 46;
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(8, 145, 178);
  doc.text('Inhaltsverzeichnis', 18, y);
  y += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  analyses.forEach((a, idx) => {
    doc.text(`${idx + 1}. ${a.fileName || `Dokument ${idx + 1}`}`, 22, y);
    y += 5;
  });
  
  // Add each analysis on new page
  for (let i = 0; i < analyses.length; i++) {
    doc.addPage();
    
    // Document header
    doc.setFillColor(240, 240, 240);
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 14, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(8, 145, 178);
    doc.text(`Dokument ${i + 1} von ${analyses.length}`, 18, 10);
    doc.setTextColor(0, 0, 0);
    
    const builder = new PdfBuilder(doc, 22);
    addAnalysisContent(builder, analyses[i]);
  }

  addFooter(doc, config);
  doc.save(`Batch_Analyse_${new Date().toISOString().split('T')[0]}.pdf`);
}

export async function exportComparisonToPDF(comparison: ComparisonData) {
  const config = getBrandingConfig();
  const doc = await createBrandedPDF('Dokumenten-Vergleich', config);
  const builder = new PdfBuilder(doc, 46);

  // Info
  builder.addSectionHeader('Vergleichsinformation');
  if (comparison.fileName1) {
    builder.addLabelValue('Dokument 1:', comparison.fileName1);
  }
  if (comparison.fileName2) {
    builder.addLabelValue('Dokument 2:', comparison.fileName2);
  }
  builder.addLabelValue('Erstellt:', formatDateDE(new Date()));
  builder.addSpace(4);

  // Summary
  if (comparison.summary) {
    builder.addSectionHeader('Zusammenfassung');
    builder.addParagraph(comparison.summary);
    builder.addSpace(3);
  }

  // Similarities
  if (comparison.similarities && comparison.similarities.length > 0) {
    builder.addSectionHeader('Gemeinsamkeiten');
    builder.addBulletList(comparison.similarities);
    builder.addSpace(3);
  }

  // Differences
  if (comparison.differences && comparison.differences.length > 0) {
    builder.addSectionHeader('Unterschiede');
    builder.addBulletList(comparison.differences);
    builder.addSpace(3);
  }

  // Unique to Doc 1
  if (comparison.uniqueDoc1 && comparison.uniqueDoc1.length > 0) {
    builder.addSectionHeader(`Nur in ${comparison.fileName1 || 'Dokument 1'}`);
    builder.addBulletList(comparison.uniqueDoc1);
    builder.addSpace(3);
  }

  // Unique to Doc 2
  if (comparison.uniqueDoc2 && comparison.uniqueDoc2.length > 0) {
    builder.addSectionHeader(`Nur in ${comparison.fileName2 || 'Dokument 2'}`);
    builder.addBulletList(comparison.uniqueDoc2);
  }

  addFooter(doc, config);
  doc.save(`Vergleich_${new Date().toISOString().split('T')[0]}.pdf`);
}

export async function exportQAToPDF(qa: QAData) {
  const config = getBrandingConfig();
  const doc = await createBrandedPDF('Frage & Antwort', config);
  const builder = new PdfBuilder(doc, 46);

  // Info
  builder.addLabelValue('Erstellt:', formatDateDE(new Date()));
  if (qa.fileName) {
    builder.addLabelValue('Dokument:', qa.fileName);
  }
  builder.addSpace(4);

  // Question
  builder.addSectionHeader('Frage');
  builder.addParagraph(qa.question);
  builder.addSpace(3);

  // Answer
  builder.addSectionHeader('Antwort');
  builder.addParagraph(qa.answer);

  addFooter(doc, config);
  doc.save(`QA_${new Date().toISOString().split('T')[0]}.pdf`);
}

// ============= OFFER PDF EXPORT =============

export interface OfferData {
  offerNumber: string;
  offerDate: Date;
  validUntil: Date;
  customer: {
    name: string;
    company?: string;
    address: string;
    email: string;
    phone?: string;
  };
  positions: Array<{
    description: string;
    quantity: number;
    unit: string;
    unitPrice: number;
  }>;
  notes?: string;
  paymentTerms?: string;
  additionalContent?: string; // HTML content for larger text blocks
  taxRate: number;
  isKleinunternehmer?: boolean; // §19 UStG - no VAT
}

// Type for parsed HTML elements
interface ParsedHtmlElement {
  type: 'heading' | 'paragraph' | 'list-item' | 'table-row' | 'space';
  text: string;
  level?: number; // for headings (h1=1, h2=2, etc.)
  bold?: boolean;
  cells?: string[]; // for table rows
}

// Advanced HTML parser that preserves formatting
function parseHtmlToElements(html: string): ParsedHtmlElement[] {
  const elements: ParsedHtmlElement[] = [];
  
  // Helper to decode HTML entities (including German umlauts and special characters)
  const decodeEntities = (text: string) => {
    return text
      // German umlauts
      .replace(/&auml;/gi, 'ä')
      .replace(/&Auml;/gi, 'Ä')
      .replace(/&ouml;/gi, 'ö')
      .replace(/&Ouml;/gi, 'Ö')
      .replace(/&uuml;/gi, 'ü')
      .replace(/&Uuml;/gi, 'Ü')
      .replace(/&szlig;/gi, 'ß')
      // German/typographic quotation marks
      .replace(/&bdquo;/gi, '\u201E')  // „
      .replace(/&ldquo;/gi, '\u201C')  // "
      .replace(/&rdquo;/gi, '\u201D')  // "
      .replace(/&sbquo;/gi, '\u201A')  // ‚
      .replace(/&lsquo;/gi, '\u2018')  // '
      .replace(/&rsquo;/gi, '\u2019')  // '
      // Common special characters
      .replace(/&ndash;/gi, '–')
      .replace(/&mdash;/gi, '—')
      .replace(/&times;/gi, '×')
      .replace(/&euro;/gi, '€')
      .replace(/&copy;/gi, '©')
      .replace(/&reg;/gi, '®')
      .replace(/&trade;/gi, '™')
      .replace(/&hellip;/gi, '…')
      .replace(/&laquo;/gi, '«')
      .replace(/&raquo;/gi, '»')
      .replace(/&bull;/gi, '•')
      .replace(/&deg;/gi, '°')
      .replace(/&plusmn;/gi, '±')
      .replace(/&frac12;/gi, '½')
      .replace(/&frac14;/gi, '¼')
      .replace(/&frac34;/gi, '¾')
      // Basic entities
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      // Numeric entities (decimal and hex)
      .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)))
      .replace(/&#x([a-fA-F0-9]+);/g, (_, code) => String.fromCharCode(parseInt(code, 16)))
      // Cleanup
      .replace(/\s+/g, ' ')
      .trim();
  };
  
  // Helper to extract text with bold markers
  const processInlineFormatting = (text: string): string => {
    // Keep track of bold/strong content by marking it
    // Use [\s\S] instead of . to match across newlines
    return text
      .replace(/<strong>([\s\S]*?)<\/strong>/gi, '**$1**')
      .replace(/<b>([\s\S]*?)<\/b>/gi, '**$1**')
      .replace(/<em>([\s\S]*?)<\/em>/gi, '_$1_')
      .replace(/<i>([\s\S]*?)<\/i>/gi, '_$1_')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<a[^>]*>([\s\S]*?)<\/a>/gi, '$1') // Extract link text
      .replace(/<[^>]+>/g, ''); // Remove remaining tags
  };

  // Parse headings - use [\s\S] to match across newlines
  const headingRegex = /<h([1-6])[^>]*>([\s\S]*?)<\/h\1>/gi;
  let workingHtml = html;
  
  // Extract headings first
  const headings: { match: string; level: number; text: string; index: number }[] = [];
  let match;
  while ((match = headingRegex.exec(html)) !== null) {
    headings.push({
      match: match[0],
      level: parseInt(match[1]),
      text: decodeEntities(processInlineFormatting(match[2])),
      index: match.index
    });
  }
  
  // Extract list items - use [\s\S] to match across newlines
  const listItemRegex = /<li[^>]*>([\s\S]*?)<\/li>/gi;
  const listItems: { match: string; text: string; index: number }[] = [];
  while ((match = listItemRegex.exec(html)) !== null) {
    listItems.push({
      match: match[0],
      text: decodeEntities(processInlineFormatting(match[1])),
      index: match.index
    });
  }
  
  // Extract table rows - use [\s\S] to match across newlines
  const tableRowRegex = /<tr[^>]*>([\s\S]*?)<\/tr>/gi;
  const tableRows: { match: string; cells: string[]; index: number }[] = [];
  while ((match = tableRowRegex.exec(html)) !== null) {
    const cellRegex = /<t[hd][^>]*>([\s\S]*?)<\/t[hd]>/gi;
    const cells: string[] = [];
    let cellMatch;
    while ((cellMatch = cellRegex.exec(match[1])) !== null) {
      cells.push(decodeEntities(processInlineFormatting(cellMatch[1])));
    }
    if (cells.length > 0) {
      tableRows.push({
        match: match[0],
        cells,
        index: match.index
      });
    }
  }
  
  // Extract paragraphs - use [\s\S] to match across newlines
  const paragraphRegex = /<p[^>]*>([\s\S]*?)<\/p>/gi;
  const paragraphs: { match: string; text: string; index: number }[] = [];
  while ((match = paragraphRegex.exec(html)) !== null) {
    paragraphs.push({
      match: match[0],
      text: decodeEntities(processInlineFormatting(match[1])),
      index: match.index
    });
  }
  
  // Extract div blocks as paragraphs - common in rich text editors
  const divRegex = /<div[^>]*>([\s\S]*?)<\/div>/gi;
  const divs: { match: string; text: string; index: number }[] = [];
  while ((match = divRegex.exec(html)) !== null) {
    // Only process divs that don't contain other block elements
    const content = match[1];
    if (!/<(p|h[1-6]|ul|ol|table|div)[^>]*>/i.test(content)) {
      divs.push({
        match: match[0],
        text: decodeEntities(processInlineFormatting(content)),
        index: match.index
      });
    }
  }
  
  // Combine all elements and sort by position in original HTML
  const allElements: { type: string; index: number; data: any }[] = [
    ...headings.map(h => ({ type: 'heading', index: h.index, data: h })),
    ...listItems.map(l => ({ type: 'list-item', index: l.index, data: l })),
    ...tableRows.map(t => ({ type: 'table-row', index: t.index, data: t })),
    ...paragraphs.map(p => ({ type: 'paragraph', index: p.index, data: p })),
    ...divs.map(d => ({ type: 'paragraph', index: d.index, data: d }))
  ].sort((a, b) => a.index - b.index);
  
  // Convert to ParsedHtmlElement array
  for (const el of allElements) {
    switch (el.type) {
      case 'heading':
        elements.push({ type: 'space', text: '' });
        elements.push({ 
          type: 'heading', 
          text: el.data.text, 
          level: el.data.level,
          bold: true 
        });
        break;
      case 'list-item':
        elements.push({ 
          type: 'list-item', 
          text: el.data.text 
        });
        break;
      case 'table-row':
        elements.push({ 
          type: 'table-row', 
          text: '', 
          cells: el.data.cells 
        });
        break;
      case 'paragraph':
        elements.push({ 
          type: 'paragraph', 
          text: el.data.text 
        });
        elements.push({ type: 'space', text: '' });
        break;
    }
  }
  
  // If no structured elements found, treat as plain text
  if (elements.length === 0) {
    const plainText = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .trim();
    
    if (plainText) {
      const lines = plainText.split(/\n+/).filter(l => l.trim());
      for (const line of lines) {
        elements.push({ type: 'paragraph', text: decodeEntities(line) });
      }
    }
  }
  
  return elements;
}

// Render parsed HTML elements to PDF
function renderHtmlToPdf(
  doc: jsPDF, 
  html: string, 
  startY: number, 
  margin: number, 
  contentWidth: number,
  pageHeight: number
): number {
  const elements = parseHtmlToElements(html);
  let y = startY;
  
  for (const element of elements) {
    // Check for page break
    if (y > pageHeight - 50) {
      doc.addPage();
      y = margin + 10;
    }
    
    switch (element.type) {
      case 'heading':
        // Style based on heading level
        const fontSize = element.level === 1 ? 14 : element.level === 2 ? 12 : 11;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fontSize);
        doc.setTextColor(8, 145, 178);
        
        const headingLines = doc.splitTextToSize(element.text, contentWidth);
        for (const line of headingLines) {
          if (y > pageHeight - 50) {
            doc.addPage();
            y = margin + 10;
          }
          doc.text(line, margin, y);
          y += fontSize * 0.5;
        }
        y += 3;
        break;
        
      case 'paragraph':
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(50, 50, 50);
        
        // Handle inline bold markers **text**
        const text = element.text;
        const paraLines = doc.splitTextToSize(text.replace(/\*\*/g, ''), contentWidth);
        
        for (const line of paraLines) {
          if (y > pageHeight - 50) {
            doc.addPage();
            y = margin + 10;
          }
          // Check if original had bold markers
          if (text.includes('**')) {
            // Render with bold segments
            renderTextWithBold(doc, line, text, margin, y);
          } else {
            doc.text(line, margin, y);
          }
          y += 4.5;
        }
        break;
        
      case 'list-item':
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(50, 50, 50);
        
        const bulletX = margin;
        const textX = margin + 6;
        const itemWidth = contentWidth - 6;
        
        // Draw bullet
        doc.setFillColor(8, 145, 178);
        doc.circle(bulletX + 1.5, y - 1.5, 1, 'F');
        
        // Text
        const itemLines = doc.splitTextToSize(element.text.replace(/\*\*/g, ''), itemWidth);
        for (let i = 0; i < itemLines.length; i++) {
          if (y > pageHeight - 50) {
            doc.addPage();
            y = margin + 10;
          }
          doc.text(itemLines[i], textX, y);
          y += 4.5;
        }
        break;
        
      case 'table-row':
        if (element.cells && element.cells.length > 0) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(50, 50, 50);
          
          const cellWidth = contentWidth / element.cells.length;
          
          // Draw cells
          for (let i = 0; i < element.cells.length; i++) {
            const cellX = margin + i * cellWidth;
            const cellText = doc.splitTextToSize(element.cells[i], cellWidth - 4);
            doc.text(cellText[0] || '', cellX + 2, y);
          }
          y += 5;
          // No separator lines - cleaner look
        }
        break;
        
      case 'space':
        y += 3;
        break;
    }
  }
  
  return y;
}

// Helper to render text with bold segments (marked with **)
function renderTextWithBold(doc: jsPDF, line: string, originalText: string, x: number, y: number) {
  // For simplicity, just render the line - bold detection would need word-level tracking
  doc.text(line, x, y);
}

// Format currency in German format
function formatCurrencyDE(amount: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

// Format date in German format (short)
function formatDateShort(date: Date): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

// Helper function to draw header on any page
function drawOfferHeader(doc: jsPDF, logo: { dataUrl: string; width: number; height: number } | null, pageWidth: number, fontName: string = 'helvetica') {
  const headerHeight = 55;
  const margin = 20;
  
  // Main header background
  doc.setFillColor(8, 145, 178);
  doc.rect(0, 0, pageWidth, headerHeight, 'F');
  
  // White triangle for angled cut-off
  doc.setFillColor(255, 255, 255);
  doc.triangle(
    -1, headerHeight + 1,
    pageWidth * 0.6 + 1, headerHeight + 1,
    -1, headerHeight - 16,
    'F'
  );
  
  // Dark accent stripe
  doc.setFillColor(6, 95, 115);
  doc.triangle(pageWidth - 45, 0, pageWidth + 1, 0, pageWidth + 1, headerHeight - 4, 'F');
  doc.triangle(pageWidth - 45, 0, pageWidth + 1, headerHeight - 4, pageWidth - 35, headerHeight - 12, 'F');
  
  // Lighter stripe
  doc.setFillColor(14, 165, 200);
  doc.triangle(pageWidth - 65, 0, pageWidth - 45, 0, pageWidth - 55, headerHeight - 8, 'F');
  doc.triangle(pageWidth - 45, 0, pageWidth - 35, headerHeight - 12, pageWidth - 55, headerHeight - 8, 'F');

  // Logo and title
  doc.setTextColor(255, 255, 255);
  if (logo) {
    const logoX = margin;
    const logoY = 8;
    const targetLogoH = 24;
    const ratio = logo.width / logo.height;
    const targetLogoW = targetLogoH * ratio;
    doc.addImage(logo.dataUrl, 'PNG', logoX, logoY, targetLogoW, targetLogoH);

    const textX = logoX + targetLogoW + 10;
    doc.setFontSize(32);
    doc.setFont(fontName, 'bold');
    doc.text('ANGEBOT', textX, 24);
  } else {
    doc.setFontSize(32);
    doc.setFont(fontName, 'bold');
    doc.text('ANGEBOT', margin, 26);
  }
  doc.setTextColor(0, 0, 0);
}

// Helper function to draw footer on any page
function drawOfferFooter(doc: jsPDF, pageWidth: number, pageHeight: number, fontName: string = 'helvetica') {
  const footerHeight = 32;
  const footerY = pageHeight - footerHeight;

  // Main footer background
  doc.setFillColor(8, 145, 178);
  doc.rect(0, footerY, pageWidth, footerHeight + 1, 'F');

  // White triangle for angled top edge
  doc.setFillColor(255, 255, 255);
  doc.triangle(-1, footerY - 1, pageWidth * 0.4 + 1, footerY - 1, -1, footerY + 13, 'F');

  // Dark accent stripe
  doc.setFillColor(6, 95, 115);
  doc.triangle(pageWidth - 50, footerY, pageWidth + 1, footerY, pageWidth + 1, footerY + footerHeight + 1, 'F');
  doc.triangle(pageWidth - 50, footerY, pageWidth + 1, footerY + footerHeight + 1, pageWidth - 40, footerY + footerHeight + 1, 'F');

  // Lighter stripe
  doc.setFillColor(14, 165, 200);
  doc.triangle(pageWidth - 70, footerY, pageWidth - 50, footerY, pageWidth - 60, footerY + footerHeight + 1, 'F');
  doc.triangle(pageWidth - 50, footerY, pageWidth - 40, footerY + footerHeight + 1, pageWidth - 60, footerY + footerHeight + 1, 'F');

  // Footer text
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont(fontName, 'bold');
  doc.text('Klartech Müritz', pageWidth / 2, footerY + 11, { align: 'center' });
  
  doc.setFont(fontName, 'normal');
  doc.setFontSize(8);
  doc.setTextColor(220, 245, 250);
  doc.text('Keine komplizierte Fachsprache. Keine versteckten Kosten.', pageWidth / 2, footerY + 18, { align: 'center' });
  
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text('kontakt@klartech-mueritz.de  |  +49 1512 8958006  |  klartech-mueritz.de', pageWidth / 2, footerY + 25, { align: 'center' });
}

export async function exportOfferToPDF(offer: OfferData) {
  const doc = new jsPDF();
  
  // Initialize UTF-8 font for German umlauts support
  await initializePdfWithUtf8Font(doc);
  const fontName = getUtf8FontName();
  
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  const headerHeight = 55;
  const footerHeight = 32;

  // Load logo
  const logoUrl = '/images/logo-klartech.png';
  const logo = await loadImageForPdf(logoUrl);

  // Draw header and footer on first page
  drawOfferHeader(doc, logo, pageWidth, fontName);
  drawOfferFooter(doc, pageWidth, pageHeight, fontName);

  let y = headerHeight + 18;

  // ========== TWO-COLUMN LAYOUT ==========
  
  // Left column: Customer info
  doc.setFontSize(9);
  doc.setFont(fontName, 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('Angebot an:', margin, y);
  y += 6;

  doc.setFont(fontName, 'bold');
  doc.setFontSize(13);
  doc.setTextColor(30, 30, 30);
  doc.text(offer.customer.name, margin, y);
  y += 6;

  doc.setFont(fontName, 'normal');
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  
  if (offer.customer.company) {
    doc.text(offer.customer.company, margin, y);
    y += 5;
  }

  // Address
  const addressLines = offer.customer.address.split('\n');
  addressLines.forEach(line => {
    doc.text(line, margin, y);
    y += 5;
  });

  doc.text(offer.customer.email, margin, y);
  y += 5;

  if (offer.customer.phone) {
    doc.text(offer.customer.phone, margin, y);
    y += 5;
  }

  // Right column: Offer details box
  const rightBoxX = pageWidth - margin - 65;
  const rightBoxY = headerHeight + 12;
  const rightBoxW = 65;
  const rightBoxH = 32;
  
  // Box with subtle shadow effect
  doc.setFillColor(240, 240, 240);
  doc.roundedRect(rightBoxX + 1, rightBoxY + 1, rightBoxW, rightBoxH, 2, 2, 'F');
  
  // Main box
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(8, 145, 178);
  doc.setLineWidth(0.8);
  doc.roundedRect(rightBoxX, rightBoxY, rightBoxW, rightBoxH, 2, 2, 'FD');
  
  let rightY = rightBoxY + 9;

  // Offer number row
  doc.setFontSize(8);
  doc.setFont(fontName, 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('Angebotsnummer:', rightBoxX + 4, rightY);
  doc.setTextColor(8, 145, 178);
  doc.setFont(fontName, 'bold');
  doc.setFontSize(9);
  doc.text(offer.offerNumber, rightBoxX + 38, rightY);
  rightY += 7;

  // Date row
  doc.setFont(fontName, 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text('Datum:', rightBoxX + 4, rightY);
  doc.setTextColor(30, 30, 30);
  doc.text(formatDateShort(offer.offerDate), rightBoxX + 38, rightY);
  rightY += 7;

  // Valid until row
  doc.setTextColor(100, 100, 100);
  doc.text('Gültig bis:', rightBoxX + 4, rightY);
  doc.setTextColor(30, 30, 30);
  doc.text(formatDateShort(offer.validUntil), rightBoxX + 38, rightY);

  // ========== POSITIONS TABLE ==========
  y = Math.max(y, rightBoxY + rightBoxH + 8) + 12;

  // Table header with solid background
  const tableHeaderHeight = 10;
  doc.setFillColor(8, 145, 178);
  doc.rect(margin, y - 6, contentWidth, tableHeaderHeight, 'F');

  doc.setFontSize(9);
  doc.setFont(fontName, 'bold');
  doc.setTextColor(255, 255, 255);
  
  // Column positions
  const colPos = margin + 4;
  const colDesc = margin + 20;
  const colQty = margin + 105;
  const colPrice = margin + 128;
  const colTotal = margin + 155;
  
  doc.text('Pos', colPos, y);
  doc.text('Beschreibung', colDesc, y);
  doc.text('Menge', colQty, y);
  doc.text('EP', colPrice, y);
  doc.text('Gesamt', colTotal, y);
  
  y += tableHeaderHeight + 3;

  doc.setTextColor(0, 0, 0);

  // Table rows
  let netTotal = 0;

  offer.positions.forEach((pos, idx) => {
    const lineTotal = pos.quantity * pos.unitPrice;
    netTotal += lineTotal;

    // Check for page break
    if (y > pageHeight - 80) {
      doc.addPage();
      y = margin + 10;
    }

    // Alternate row background
    if (idx % 2 === 0) {
      doc.setFillColor(248, 252, 254);
      doc.rect(margin, y - 4, contentWidth, 8, 'F');
    }

    doc.setFontSize(9);
    
    // Position number in brand color
    doc.setTextColor(8, 145, 178);
    doc.setFont(fontName, 'bold');
    doc.text(String(idx + 1), colPos, y);
    
    // Description
    doc.setTextColor(40, 40, 40);
    doc.setFont(fontName, 'normal');
    const descLines = doc.splitTextToSize(pos.description, 80);
    doc.text(descLines[0], colDesc, y);
    
    // Quantity
    doc.text(`${pos.quantity} ${pos.unit}`, colQty, y);
    
    // Unit price
    doc.text(formatCurrencyDE(pos.unitPrice), colPrice, y);
    
    // Total in bold with brand color
    doc.setFont(fontName, 'bold');
    doc.setTextColor(8, 145, 178);
    doc.text(formatCurrencyDE(lineTotal), colTotal, y);

    y += 9;

    // Additional description lines
    if (descLines.length > 1) {
      doc.setFont(fontName, 'normal');
      doc.setTextColor(60, 60, 60);
      doc.setFontSize(8);
      for (let i = 1; i < descLines.length; i++) {
        doc.text(descLines[i], colDesc, y - 5);
        y += 4;
      }
    }
  });

  // Separator line
  y += 4;
  doc.setDrawColor(8, 145, 178);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 12;

  // ========== TOTALS BOX ==========
  const isKleinunternehmer = offer.isKleinunternehmer ?? false;
  const taxAmount = isKleinunternehmer ? 0 : netTotal * (offer.taxRate / 100);
  const grossTotal = netTotal + taxAmount;
  
  const totalsBoxWidth = 80;
  const totalsBoxX = pageWidth - margin - totalsBoxWidth;
  const totalsBoxY = y - 4;
  const totalsBoxHeight = isKleinunternehmer ? 22 : 38;

  // Box background with border
  doc.setFillColor(248, 252, 254);
  doc.setDrawColor(8, 145, 178);
  doc.setLineWidth(0.4);
  doc.roundedRect(totalsBoxX - 3, totalsBoxY, totalsBoxWidth + 3, totalsBoxHeight, 2, 2, 'FD');

  const labelX = totalsBoxX + 2;
  const valueX = totalsBoxX + totalsBoxWidth - 8;

  doc.setFontSize(10);
  
  if (!isKleinunternehmer) {
    // Netto
    doc.setFont(fontName, 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('Netto:', labelX, y);
    doc.setTextColor(40, 40, 40);
    doc.text(formatCurrencyDE(netTotal), valueX, y, { align: 'right' });
    y += 7;

    // MwSt
    doc.setTextColor(80, 80, 80);
    doc.text(`MwSt. ${offer.taxRate}%:`, labelX, y);
    doc.setTextColor(40, 40, 40);
    doc.text(formatCurrencyDE(taxAmount), valueX, y, { align: 'right' });
    y += 8;

    // Separator in totals box
    doc.setDrawColor(8, 145, 178);
    doc.setLineWidth(0.3);
    doc.line(totalsBoxX, y - 3, totalsBoxX + totalsBoxWidth - 5, y - 3);

    // Gesamt - bold and prominent
    doc.setFont(fontName, 'bold');
    doc.setFontSize(12);
    doc.setTextColor(8, 145, 178);
    doc.text('Gesamt:', labelX, y + 2);
    doc.text(formatCurrencyDE(grossTotal), valueX, y + 2, { align: 'right' });
  } else {
    // Kleinunternehmer - just total
    doc.setFont(fontName, 'bold');
    doc.setFontSize(12);
    doc.setTextColor(8, 145, 178);
    doc.text('Gesamt:', labelX, y + 4);
    doc.text(formatCurrencyDE(netTotal), valueX, y + 4, { align: 'right' });
  }

  y = totalsBoxY + totalsBoxHeight + 10;

  // §19 UStG notice for Kleinunternehmer
  if (isKleinunternehmer) {
    doc.setFont(fontName, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    const kleinunternehmerText = 'Gemäß § 19 UStG wird keine Umsatzsteuer berechnet (Kleinunternehmerregelung).';
    doc.text(kleinunternehmerText, margin, y);
    y += 10;
  }

  // ========== PAYMENT TERMS & NOTES ==========
  if (offer.paymentTerms) {
    doc.setFont(fontName, 'bold');
    doc.setFontSize(10);
    doc.setTextColor(8, 145, 178);
    doc.text('Zahlungsbedingungen:', margin, y);
    y += 5;
    
    doc.setFont(fontName, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    const paymentLines = doc.splitTextToSize(offer.paymentTerms, contentWidth);
    doc.text(paymentLines, margin, y);
    y += paymentLines.length * 4.5 + 8;
  }

  if (offer.notes) {
    doc.setFont(fontName, 'bold');
    doc.setFontSize(10);
    doc.setTextColor(8, 145, 178);
    doc.text('Hinweise:', margin, y);
    y += 5;
    
    doc.setFont(fontName, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    const notesLines = doc.splitTextToSize(offer.notes, contentWidth);
    doc.text(notesLines, margin, y);
    y += notesLines.length * 4.5 + 8;
  }

  // ========== ADDITIONAL CONTENT (HTML) - ALWAYS ON PAGE 2 ==========
  if (offer.additionalContent && offer.additionalContent.trim()) {
    // Always start on a new page for additional content
    doc.addPage();
    
    // Draw header and footer on new page
    drawOfferHeader(doc, logo, pageWidth, fontName);
    drawOfferFooter(doc, pageWidth, pageHeight, fontName);
    
    y = headerHeight + 15;
    
    doc.setFont(fontName, 'bold');
    doc.setFontSize(12);
    doc.setTextColor(8, 145, 178);
    doc.text('Weitere Informationen:', margin, y);
    y += 10;
    
    // Use the advanced HTML renderer with page-aware rendering
    const maxY = pageHeight - footerHeight - 10;
    y = renderHtmlToPdfWithBranding(doc, offer.additionalContent, y, margin, contentWidth, pageHeight, maxY, logo, pageWidth, headerHeight, footerHeight, fontName);
  }

  // Save PDF
  const fileName = `Angebot_${offer.offerNumber}_${offer.customer.name.replace(/\s+/g, '_')}.pdf`;
  doc.save(fileName);
}

// Modified HTML renderer that adds header/footer on new pages
function renderHtmlToPdfWithBranding(
  doc: jsPDF,
  html: string,
  startY: number,
  margin: number,
  contentWidth: number,
  pageHeight: number,
  maxY: number,
  logo: { dataUrl: string; width: number; height: number } | null,
  pageWidth: number,
  headerHeight: number,
  footerHeight: number,
  fontName: string = 'helvetica'
): number {
  const elements = parseHtmlToElements(html);
  let y = startY;

  for (const element of elements) {
    // Check if we need a new page
    const estimatedHeight = element.type === 'heading' ? 14 : element.type === 'table-row' ? 30 : 8;
    if (y + estimatedHeight > maxY) {
      doc.addPage();
      drawOfferHeader(doc, logo, pageWidth, fontName);
      drawOfferFooter(doc, pageWidth, pageHeight, fontName);
      y = headerHeight + 15;
    }

    switch (element.type) {
      case 'heading':
        const fontSize = element.level === 1 ? 16 : element.level === 2 ? 14 : 12;
        doc.setFont(fontName, 'bold');
        doc.setFontSize(fontSize);
        doc.setTextColor(8, 145, 178);
        doc.text(element.text, margin, y);
        y += fontSize * 0.6 + 2;
        break;

      case 'paragraph':
        doc.setFont(fontName, 'normal');
        doc.setFontSize(10);
        doc.setTextColor(50, 50, 50);
        const lines = doc.splitTextToSize(element.text.replace(/\*\*/g, ''), contentWidth);
        for (const line of lines) {
          if (y > maxY) {
            doc.addPage();
            drawOfferHeader(doc, logo, pageWidth, fontName);
            drawOfferFooter(doc, pageWidth, pageHeight, fontName);
            y = headerHeight + 15;
          }
          doc.text(line, margin, y);
          y += 5;
        }
        y += 2;
        break;

      case 'list-item':
        doc.setFont(fontName, 'normal');
        doc.setFontSize(10);
        doc.setTextColor(50, 50, 50);
        const bulletX = margin + 3;
        doc.setFillColor(8, 145, 178);
        doc.circle(bulletX, y - 1.5, 1, 'F');
        const liLines = doc.splitTextToSize(element.text.replace(/\*\*/g, ''), contentWidth - 10);
        for (let i = 0; i < liLines.length; i++) {
          if (y > maxY) {
            doc.addPage();
            drawOfferHeader(doc, logo, pageWidth, fontName);
            drawOfferFooter(doc, pageWidth, pageHeight, fontName);
            y = headerHeight + 15;
          }
          doc.text(liLines[i], margin + 8, y);
          y += 5;
        }
        break;

      case 'table-row':
        if (element.cells && element.cells.length > 0) {
          doc.setFont(fontName, 'normal');
          doc.setFontSize(9);
          doc.setTextColor(50, 50, 50);
          const cellWidth = contentWidth / element.cells.length;
          element.cells.forEach((cell, idx) => {
            const cellX = margin + idx * cellWidth;
            const cellText = doc.splitTextToSize(cell, cellWidth - 4);
            doc.text(cellText[0] || '', cellX + 2, y);
          });
          y += 5;
        }
        break;

      case 'space':
        y += 3;
        break;

      default:
        if (element.text && element.text.trim()) {
          doc.setFont(fontName, 'normal');
          doc.setFontSize(10);
          doc.setTextColor(50, 50, 50);
          const defaultLines = doc.splitTextToSize(element.text, contentWidth);
          for (const line of defaultLines) {
            if (y > maxY) {
              doc.addPage();
              drawOfferHeader(doc, logo, pageWidth, fontName);
              drawOfferFooter(doc, pageWidth, pageHeight, fontName);
              y = headerHeight + 15;
            }
            doc.text(line, margin, y);
            y += 5;
          }
        }
    }
  }

  return y;
}

// ============= UTILITY FUNCTIONS =============

// Copy to clipboard helper
export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text);
}

// Format analysis for text export
export function formatAnalysisAsText(analysis: AnalysisData): string {
  let text = '=== KLARTECH KOMPAKT - ANALYSE ===\n\n';

  if (analysis.fileName) {
    text += `Dateiname: ${analysis.fileName}\n`;
  }
  text += `Datum: ${new Date().toLocaleString('de-DE')}\n\n`;

  if (analysis.summary) {
    text += `--- ZUSAMMENFASSUNG ---\n${analysis.summary}\n\n`;
  }

  if (analysis.keywords?.length) {
    text += `--- KEYWORDS ---\n${analysis.keywords.join(', ')}\n\n`;
  }

  if (analysis.sentiment) {
    text += `--- SENTIMENT ---\n`;
    text += `Gesamt: ${analysis.sentiment.overall}\n`;
    text += `Positiv: ${analysis.sentiment.positive}%\n`;
    text += `Neutral: ${analysis.sentiment.neutral}%\n`;
    text += `Negativ: ${analysis.sentiment.negative}%\n\n`;
  }

  if (analysis.themes?.length) {
    text += `--- THEMEN ---\n`;
    analysis.themes.forEach((t) => {
      text += `• ${t.theme} (${t.sentiment})\n`;
    });
  }

  return text;
}

// Format comparison for text export
export function formatComparisonAsText(comparison: ComparisonData): string {
  let text = '=== KLARTECH KOMPAKT - VERGLEICH ===\n\n';

  text += `Dokument 1: ${comparison.fileName1 || 'Unbenannt'}\n`;
  text += `Dokument 2: ${comparison.fileName2 || 'Unbenannt'}\n`;
  text += `Datum: ${new Date().toLocaleString('de-DE')}\n\n`;

  if (comparison.summary) {
    text += `--- ZUSAMMENFASSUNG ---\n${comparison.summary}\n\n`;
  }

  if (comparison.similarities?.length) {
    text += `--- GEMEINSAMKEITEN ---\n`;
    comparison.similarities.forEach((s) => (text += `• ${s}\n`));
    text += '\n';
  }

  if (comparison.differences?.length) {
    text += `--- UNTERSCHIEDE ---\n`;
    comparison.differences.forEach((d) => (text += `• ${d}\n`));
    text += '\n';
  }

  return text;
}
